import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class ZplService {
  // Zebra Browser Print local endpoint
  private readonly baseUrl = 'http://localhost:9100'; 

  constructor(private http: HttpClient) {}

  /**
   * Main function to generate the ZPL string for Zebra Printers.
   * Handles: Tail Offsets, Human-readable Barcodes, and Dynamic Scaling.
   */
  generateZPL(
    tags: any[],
    fieldPositions: { [key: string]: { x: number; y: number } },
    fieldVisibility: { [key: string]: boolean },
    customLabels: { [key: string]: string },
    settings: any,
    containerWidthPx: number, // The width of the white printable area in the designer
    containerHeightPx: number,
    printerDpi: number = 203
  ): string {

    // 1. Convert physical dimensions to Printer Dots
    const totalWidthDots = this.convertToDots(settings.labelWidth, printerDpi);
    const labelHeightDots = this.convertToDots(settings.labelHeight, printerDpi);
    
    // Calculate Tail Dots (e.g., 35mm -> dots)
    const tailDots = (settings.tailSide !== 'none') ? this.convertToDots(settings.tailLength, printerDpi) : 0;
    
    // Calculate Printable Area in Dots
    const printableWidthDots = totalWidthDots - tailDots;

    // Offset Logic: If tail is on the left, start printing AFTER the tail
    const xOffsetDots = (settings.tailSide === 'left') ? tailDots : 0;

    let zpl = '';

    tags.forEach(tag => {
      // --- Start Label ---
      zpl += `^XA`;
      zpl += `^PW${totalWidthDots}`;      // Total Physical Width
      zpl += `^LL${labelHeightDots}`;      // Physical Height
      zpl += `^MD15`;                      // Darkness (0 to 30)
      zpl += `^LH${xOffsetDots},0`;        // Label Home (Moves entire origin)
      zpl += `^CI28`;                      // UTF-8 Support

      Object.keys(fieldPositions).forEach(key => {
        if (fieldVisibility[key] && fieldPositions[key]) {
          const pos = fieldPositions[key];

          /**
           * Scaling Logic:
           * containerWidthPx represents the white area in your designer (e.g., 65mm or 260px).
           * We calculate the percentage of position and apply it to printableWidthDots.
           */
          let xPercent = pos.x / containerWidthPx;
          let yPercent = pos.y / containerHeightPx;

          const xDot = Math.round(xPercent * printableWidthDots);
          const yDot = Math.round(yPercent * labelHeightDots);

          // --- Barcode Element ---
          if (key === 'barcode') {
            const heightSetting = settings.barcodeSize || '20';
            const barHeight = Math.round(this.convertToDots(heightSetting, printerDpi));
            
            /**
             * ^BC Parameters: Orientation, Height, Interpretation Line (Y=Yes), 
             * Line Above (N), Check Digit (N), Mode (N).
             */
            zpl += `^FO${xDot},${yDot}^BY1,2,10^BCN,${barHeight},Y,N,N^FD${tag.barcode}^FS`;
          } 
          // --- Text Elements ---
          else {
            let value = this.formatValue(tag, key);
            
            // Build Display String (Label: Value)
            const labelPrefix = customLabels[key] ? `${customLabels[key]}: ` : '';
            const fullDisplayText = `${labelPrefix}${value}`;

            // Font Size Calculation
            const formFontSize = parseFloat(settings[key + 'Size'] || '10'); 
            
            /**
             * Thermal Printing Scaling Fix:
             * Converting screen pixels/points to printer dots.
             * Standard Zebra A0 font needs H and W.
             */
            const zplFontSize = Math.round(formFontSize * (printerDpi / 72)); 

            zpl += `^FO${xDot},${yDot}^A0N,${zplFontSize},${zplFontSize}^FD${fullDisplayText}^FS`;
          }
        }
      });

      // --- End Label ---
      zpl += `^XZ`;
    });

    return zpl;
  }

  /**
   * Helper: Parse various unit types to Printer Dots
   */
  private convertToDots(value: string | number, dpi: number): number {
    if (!value) return 0;
    const str = String(value).toLowerCase().trim();
    
    let mm = 0;
    if (str.endsWith('mm')) {
      mm = parseFloat(str.replace('mm', ''));
    } else if (str.endsWith('px')) {
      // Screen PX (96dpi) to MM
      mm = parseFloat(str.replace('px', '')) * (25.4 / 96);
    } else if (str.endsWith('in')) {
      mm = parseFloat(str.replace('in', '')) * 25.4;
    } else {
      mm = parseFloat(str); // Default to MM
    }

    if (isNaN(mm)) return 0;

    // Formula: (mm / 25.4) * DPI
    return Math.round((mm / 25.4) * dpi);
  }

  /**
   * Data Formatter for ZPL display strings
   */
  private formatValue(tag: any, key: string): string {
     if (!tag[key]) return '';
     
     if(key === 'unitPrice') {
         // Standardize currency symbol for Zebra printers (Rs. is safest)
         return `Rs.${tag[key]}`;
     }
     if(key === 'totalWeight') {
         return `${tag[key]} g`;
     }
     return tag[key];
  }

  /**
   * Communication with Zebra Browser Print Desktop App
   */
  async printToLocalZebra(zpl: string) {
    try {
      const response = await fetch(`${this.baseUrl}/available`);
      const devices = await response.json();

      if (!devices || !devices.printer || devices.printer.length === 0) {
        alert('Zebra Printer not detected. Please ensure Browser Print is running.');
        return;
      }

      // Preference logic: find a driver-based printer first
      const selectedPrinter = devices.printer.find((p: any) => p.connection === 'driver') || devices.printer[0];
      
      const payload = {
        device: {
          deviceType: 'printer',
          uid: selectedPrinter.uid,
          name: selectedPrinter.name,
          connection: selectedPrinter.connection,
          provider: selectedPrinter.provider,
          manufacturer: selectedPrinter.manufacturer,
          version: selectedPrinter.version
        },
        action: 'print',
        data: zpl
      };

      const printResult = await fetch(`${this.baseUrl}/write`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(payload)
      });

      if (!printResult.ok) {
        throw new Error(`HTTP Error: ${printResult.status}`);
      }
      
      console.log('ZPL successfully sent to printer.');

    } catch (error) {
      console.error('ZPL Print Error:', error);
      alert('Printing failed. Check console for details.');
      this.downloadPrnFile(zpl); // Fallback to download
    }
  }

  /**
   * Fallback: Download a .prn file for manual printing
   */
  downloadPrnFile(zplData: string) {
    const blob = new Blob([zplData], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `barcode_label_${new Date().getTime()}.prn`;
    a.click();
    window.URL.revokeObjectURL(url);
  }
}